import type { ColumnDef } from "@tanstack/react-table";
import { capitalize, highlightText } from "@repo/utils";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import type { PaymentMethod } from "~/providers/payment-method-provider";
import { SettingsHeader } from "~/components/layout/settings-header";
import { DataTable } from "~/components/miscellaneous/data-table";
import { DataTableColumnHeader } from "~/components/miscellaneous/data-table-column-header";
import { usePaymentMethod } from "~/hooks/use-payment-method";
import { usePaymentMethodManager } from "~/managers/use-payment-method-manager";

export const Route = createFileRoute("/_main/settings/_layout/payment-methods")(
  {
    component: PaymentMethods,
  },
);

function PaymentMethods() {
  const { t } = useTranslation("common");
  const { paymentMethods } = usePaymentMethod();
  const { openPaymentMethodDialog } = usePaymentMethodManager();
  const columns: ColumnDef<PaymentMethod>[] = [
    {
      id: t("name"),
      accessorKey: "name",
      filterFn: "includesString",
      size: 200,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("name")} />
      ),
      cell: ({ row: { original: paymentMethod }, table }) => {
        const globalFilter = table.getState().globalFilter as string;

        return (
          <p
            dangerouslySetInnerHTML={{
              __html: highlightText(paymentMethod.name, globalFilter) ?? "",
            }}
          ></p>
        );
      },
    },
    {
      id: t("position"),
      accessorKey: "position",
      filterFn: "includesString",
      size: 200,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("position")} />
      ),
      cell: ({ row: { original: paymentMethod }, table }) => {
        const globalFilter = table.getState().globalFilter as string;

        return (
          <p
            dangerouslySetInnerHTML={{
              __html:
                highlightText(
                  paymentMethod.position.toString(),
                  globalFilter,
                ) ?? "",
            }}
          ></p>
        );
      },
    },
  ];

  return (
    <>
      <SettingsHeader
        title={t("payment_methods")}
        buttonLabel={capitalize(t("new_payment_method"))}
        buttonAction={() => {
          void openPaymentMethodDialog();
        }}
      />

      <DataTable
        defaultSorting={[{ id: t("position"), desc: false }]}
        columns={columns}
        data={paymentMethods}
        onSelect={openPaymentMethodDialog}
      />
    </>
  );
}
